import type { DateValue } from "react-aria-components";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import dayjs from "@/lib/helpers/dayjs";
import type { Note } from "@/core/types";

const NOTE_TYPE_PREDICATES: Record<string, (note: Note) => boolean> = {
  "Advanced Care Activity": (note) => note.note_type === "Advanced Care Activity",
  all: () => true,
  BHS: (note) => note.provider_type?.includes("BEHAVIORAL") ?? false,
  "Care Communication": (note) => note.note_type === "Care Communication",
  "Care Coordination": (note) => note.note_type === "Care Coordination",
  "Care Management": (note) => note.note_type === "Care Management",
  CASE_MANAGER: (note) => note.provider_type === "CASE_MANAGER",
  "Case Management": (note) => note.note_type === "Case Management",
  "Check-in": (note) => note.note_type === "Check-in",
  PEER: (note) => note.provider_type === "PEER",
  PRESCRIBE: (note) => note.provider_type === "PRESCRIBE",
};

export function useNotesFilter(notes: Note[] | undefined) {
  const [noteType, setNoteType] = useState("all");
  const [dateStart, setDateStart] = useState<DateValue | null>(null);
  const [dateEnd, setDateEnd] = useState<DateValue | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState<Nullable<string>>(null);

  const scheduleSearchFilter = useRef(
    debounce((value: Nullable<string>) => setDebouncedSearch(value), 300),
  ).current;

  const handleSetSearch = useCallback(
    (value: string) => {
      setSearch(value);
      scheduleSearchFilter(value);
    },
    [scheduleSearchFilter],
  );

  useEffect(() => () => scheduleSearchFilter.cancel(), [scheduleSearchFilter]);

  const isDirty = useMemo(() => {
    return noteType !== "all" || !!dateStart || !!dateEnd || !!search;
  }, [noteType, dateStart, dateEnd, search]);

  const filterNotes = useCallback(
    (_notes: Note[]) => {
      const typePredicate = NOTE_TYPE_PREDICATES[noteType] ?? (() => true);

      const start = dateStart
        ? dayjs(new Date(dateStart.year, dateStart.month - 1, dateStart.day))
        : null;

      const end = dateEnd ? dayjs(new Date(dateEnd.year, dateEnd.month - 1, dateEnd.day)) : null;
      const searchLower = debouncedSearch?.toLowerCase() ?? null;

      return _notes.filter((note) => {
        if (!typePredicate(note)) return false;

        if (
          searchLower &&
          !note.title?.toLowerCase().includes(searchLower) &&
          !note.signature?.toLowerCase().includes(searchLower)
        )
          return false;

        if (start || end) {
          const noteDate = dayjs(note.session_date, "MM/DD/YYYY");

          if (!noteDate.isValid()) return true;

          if (start && !noteDate.isSameOrAfter(start, "day")) return false;

          if (end && !noteDate.isSameOrBefore(end, "day")) return false;
        }

        return true;
      });
    },
    [dateEnd, dateStart, noteType, debouncedSearch],
  );

  const filteredNotes = useMemo(() => filterNotes(notes ?? []), [filterNotes, notes]);

  const clearAll = useCallback(() => {
    setDateStart(null);
    setDateEnd(null);
    setNoteType("all");
    setSearch("");
    setDebouncedSearch("");
  }, []);

  return {
    clearAll,
    dateEnd,
    dateStart,
    filteredNotes,
    isDirty,
    search,
    setDateEnd,
    setDateStart,
    setNoteType,
    setSearch: handleSetSearch,
  };
}
